import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, Plus, Search, ListTodo, CheckCircle2, Clock3, PlayCircle,
  Edit2, Trash2, X, Save, RefreshCw, ChevronLeft, ChevronRight, Lock, Unlock,
  MessageSquarePlus, Printer, Download, FileSpreadsheet
} from 'lucide-react';
import * as XLSX from 'xlsx';
import config from '../config';
import { askDeleteCode, deleteHeaders } from '../deleteConfirm';

const API = `${config.PASSPORT_API_URL}/api/work-lists`;
const PAGE_SIZE = 25;
const COLORS = ['#2563EB','#059669','#7C3AED','#EA580C','#DC2626','#0891B2','#475569'];

const getUser = () => {
  try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
};
const getPermissions = () => {
  try { return JSON.parse(getUser()?.Permissions || '{}') || {}; } catch { return {}; }
};
const getUserId = () => getUser()?.UserId || null;

const parsePassportPhones = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
  } catch {}
  return String(value).split(/[،,|/]/).map(v=>v.trim()).filter(Boolean);
};

const getPhones = (row) => {
  if (row.SourceType === 'TEST') {
    return [row.PhoneNumber,row.PhoneNumber2,row.PhoneNumber3].map(v=>String(v||'').trim()).filter(Boolean);
  }
  return parsePassportPhones(row.PassportPhoneNumbers);
};

const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, ch => ({'&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;'}[ch]));

const Modal = ({open,onClose,title,children,footer,width='max-w-lg'}) => {
  if(!open) return null;
  return <div className="fixed inset-0 z-[1200] bg-black/55 backdrop-blur-sm flex items-center justify-center p-4" dir="rtl">
    <div className={`w-full ${width} max-h-[92vh] rounded-3xl bg-white shadow-2xl overflow-hidden flex flex-col`}>
      <div className="px-5 py-4 bg-slate-50 border-b flex items-center justify-between"><h2 className="font-black text-lg">{title}</h2><button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-200"><X size={20}/></button></div>
      <div className="p-5 overflow-y-auto flex-1">{children}</div>
      {footer&&<div className="px-5 py-4 bg-slate-50 border-t">{footer}</div>}
    </div>
  </div>;
};

const WorkLists = () => {
  const navigate = useNavigate();
  const permissions = getPermissions();
  const actionPerms = permissions['إدارة قوائم العمل'];
  const can = (name, fallbackNames=[]) => {
    if(!actionPerms || !Object.keys(actionPerms).length) return true;
    if(Object.prototype.hasOwnProperty.call(actionPerms,name)) return actionPerms[name] === true;
    return fallbackNames.some(key=>actionPerms[key] === true);
  };

  const listsRequestRef = useRef(null);
  const itemsRequestRef = useRef(null);

  const [lists,setLists] = useState([]);
  const [loading,setLoading] = useState(true);
  const [message,setMessage] = useState(null);
  const [listSearch,setListSearch] = useState('');
  const [listFilter,setListFilter] = useState('open');
  const [activeList,setActiveList] = useState(null);

  const [items,setItems] = useState([]);
  const [itemsLoading,setItemsLoading] = useState(false);
  const [itemSearch,setItemSearch] = useState('');
  const [itemStatusFilter,setItemStatusFilter] = useState('');
  const [page,setPage] = useState(1);
  const [pagination,setPagination] = useState({page:1,pageSize:PAGE_SIZE,total:0,totalPages:1,from:0,to:0});

  const [showListModal,setShowListModal] = useState(false);
  const [editingList,setEditingList] = useState(null);
  const [listForm,setListForm] = useState({ListName:'',ColorHex:'#2563EB'});
  const [saving,setSaving] = useState(false);

  const [notesRow,setNotesRow] = useState(null);
  const [notes,setNotes] = useState([]);
  const [notesLoading,setNotesLoading] = useState(false);
  const [newNote,setNewNote] = useState('');
  const [noteSaving,setNoteSaving] = useState(false);
  const [exporting,setExporting] = useState(false);

  const alert = (text,type='success') => {
    setMessage({text,type});
    setTimeout(()=>setMessage(null),4500);
  };

  const confirmDelete123 = (description) => {
    const code = askDeleteCode(`${description}\n\nللتأكيد اكتب رمز تأكيد الحذف`);
    if(code === null) return false;
    if(!String(code).trim()) {
      alert('رمز التأكيد غير صحيح — لم يتم الحذف','error');
      return false;
    }
    return true;
  };

  const api = async (url,options={}) => {
    const response = await fetch(url,options);
    const data = await response.json().catch(()=>({}));
    if(!response.ok) {
      const err = new Error(data?.error || 'حدث خطأ في العملية');
      err.status = response.status;
      throw err;
    }
    return data;
  };

  const loadLists = async () => {
    if(!can('عرض قوائم العمل')) return;
    listsRequestRef.current?.abort?.();
    const controller = new AbortController();
    listsRequestRef.current = controller;
    try{
      setLoading(true);
      const params = new URLSearchParams();
      if(listFilter) params.set('status',listFilter);
      if(listSearch.trim()) params.set('search',listSearch.trim());
      const data = await api(`${API}?${params.toString()}`,{signal:controller.signal});
      const rows = Array.isArray(data?.data)?data.data:[];
      setLists(rows);
      if(activeList){
        const fresh = rows.find(x=>Number(x.WorkListID)===Number(activeList.WorkListID));
        if(fresh) setActiveList(fresh);
      }
    }catch(e){if(e?.name!=='AbortError') alert(e.message,'error');}
    finally{
      if(listsRequestRef.current===controller){listsRequestRef.current=null;setLoading(false);}
    }
  };

  const loadItems = async (pageOverride=page) => {
    if(!activeList?.WorkListID || !can('فتح قائمة')) return;
    itemsRequestRef.current?.abort?.();
    const controller = new AbortController();
    itemsRequestRef.current = controller;
    try{
      setItemsLoading(true);
      const params = new URLSearchParams({page:String(pageOverride),pageSize:String(PAGE_SIZE)});
      if(itemSearch.trim()) params.set('search',itemSearch.trim());
      if(itemStatusFilter) params.set('workStatus',itemStatusFilter);
      const data = await api(`${API}/${activeList.WorkListID}/items?${params.toString()}`,{signal:controller.signal});
      const rows = Array.isArray(data?.data)?data.data:[];
      setItems(rows);
      setPagination(data?.pagination || {page:pageOverride,pageSize:PAGE_SIZE,total:rows.length,totalPages:1,from:rows.length?1:0,to:rows.length});
      setPage(data?.pagination?.page || pageOverride);
    }catch(e){if(e?.name!=='AbortError') alert(e.message,'error');}
    finally{
      if(itemsRequestRef.current===controller){itemsRequestRef.current=null;setItemsLoading(false);}
    }
  };

  useEffect(()=>{loadLists();},[listFilter]);
  useEffect(()=>{const t=setTimeout(loadLists,280);return()=>clearTimeout(t);},[listSearch]);
  useEffect(()=>{if(activeList){setPage(1);loadItems(1);}},[activeList?.WorkListID,itemStatusFilter]);
  useEffect(()=>{if(!activeList) return; const t=setTimeout(()=>{setPage(1);loadItems(1);},320);return()=>clearTimeout(t);},[itemSearch]);
  useEffect(()=>()=>{listsRequestRef.current?.abort?.();itemsRequestRef.current?.abort?.();},[]);

  const openCreate = () => {
    setEditingList(null);
    setListForm({ListName:'',ColorHex:'#2563EB'});
    setShowListModal(true);
  };
  const openEdit = (list) => {
    setEditingList(list);
    setListForm({ListName:list.ListName||'',ColorHex:list.ColorHex||'#2563EB'});
    setShowListModal(true);
  };

  const saveList = async () => {
    if(!listForm.ListName.trim()) return alert('اكتب اسم قائمة العمل','error');
    try{
      setSaving(true);
      if(editingList){
        const data=await api(`${API}/${editingList.WorkListID}`,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify(listForm)});
        if(activeList?.WorkListID===editingList.WorkListID) setActiveList(prev=>({...prev,...data.data}));
        alert('تم تعديل القائمة');
      }else{
        const data=await api(API,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({...listForm,CreatedByUserID:getUserId()})});
        alert('تم إنشاء قائمة العمل');
        if(data?.data) setActiveList(data.data);
      }
      setShowListModal(false);
      await loadLists();
    }catch(e){alert(e.message,'error');}
    finally{setSaving(false);}
  };

  const toggleListStatus = async () => {
    if(!activeList) return;
    const next=activeList.ListStatus==='closed'?'open':'closed';
    try{
      const data=await api(`${API}/${activeList.WorkListID}`,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify({ListStatus:next})});
      setActiveList(prev=>({...prev,...data.data}));
      alert(next==='closed'?'تم إغلاق القائمة':'تم فتح القائمة');
      await loadLists();
    }catch(e){alert(e.message,'error');}
  };

  const deleteList = async (list) => {
    if(!confirmDelete123(`سيتم حذف قائمة «${list.ListName}». سيتم حذف التجميع فقط ولن تُحذف بيانات الأشخاص الأصلية.`)) return;
    try{
      await api(`${API}/${list.WorkListID}`,{method:'DELETE',headers:deleteHeaders()});
      if(activeList?.WorkListID===list.WorkListID) setActiveList(null);
      alert('تم حذف القائمة');
      await loadLists();
    }catch(e){alert(e.message,'error');}
  };

  const adjustCountsLocally = (oldStatus,newStatus) => {
    if(!activeList || oldStatus===newStatus) return;
    const keyMap={pending:'PendingItems',doing:'DoingItems',done:'DoneItems'};
    setActiveList(prev=>{
      const next={...prev};
      if(keyMap[oldStatus]) next[keyMap[oldStatus]]=Math.max(0,Number(next[keyMap[oldStatus]]||0)-1);
      if(keyMap[newStatus]) next[keyMap[newStatus]]=Number(next[keyMap[newStatus]]||0)+1;
      return next;
    });
  };

  const updateItem = async (row,patch,successText='تم التحديث') => {
    try{
      const data=await api(`${API}/items/${row.WorkListItemID}`,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify(patch)});
      setItems(prev=>prev.map(item=>item.WorkListItemID===row.WorkListItemID?{...item,...data.data}:item));
      if(patch.WorkStatus) adjustCountsLocally(row.WorkStatus,patch.WorkStatus);
      alert(successText);
    }catch(e){alert(e.message,'error');}
  };

  const removeItem = async (row) => {
    if(!confirmDelete123(`سيتم إزالة «${row.NameArabic||'هذا الشخص'}» من قائمة العمل.`)) return;
    try{
      await api(`${API}/items/${row.WorkListItemID}`,{method:'DELETE',headers:deleteHeaders()});
      alert('تمت إزالة الشخص من القائمة');
      await Promise.all([loadItems(page),loadLists()]);
    }catch(e){alert(e.message,'error');}
  };

  const openNotes = async (row) => {
    if(!can('عرض الملاحظات',['تحديث حالة الشخص'])) return;
    setNotesRow(row);
    setNewNote('');
    setNotes([]);
    try{
      setNotesLoading(true);
      const data=await api(`${API}/items/${row.WorkListItemID}/notes`);
      setNotes(Array.isArray(data?.data)?data.data:[]);
    }catch(e){alert(e.message,'error');}
    finally{setNotesLoading(false);}
  };

  const addNote = async () => {
    const text=newNote.trim();
    if(!notesRow || !text) return;
    try{
      setNoteSaving(true);
      const data=await api(`${API}/items/${notesRow.WorkListItemID}/notes`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({NoteText:text,CreatedByUserID:getUserId()})});
      setNotes(prev=>[data.data,...prev]);
      setItems(prev=>prev.map(row=>row.WorkListItemID===notesRow.WorkListItemID?{...row,ItemNote:text,NoteCount:Number(row.NoteCount||0)+1}:row));
      setNotesRow(prev=>({...prev,ItemNote:text,NoteCount:Number(prev?.NoteCount||0)+1}));
      setNewNote('');
      alert('تمت إضافة الملاحظة');
    }catch(e){alert(e.message,'error');}
    finally{setNoteSaving(false);}
  };

  const deleteNote = async (note) => {
    if(!notesRow || !confirmDelete123('سيتم حذف هذه الملاحظة من سجل الشخص.')) return;
    try{
      await api(`${API}/items/${notesRow.WorkListItemID}/notes/${note.NoteID}`,{method:'DELETE',headers:deleteHeaders()});
      const data=await api(`${API}/items/${notesRow.WorkListItemID}/notes`);
      const fresh=Array.isArray(data?.data)?data.data:[];
      setNotes(fresh);
      const latest=fresh[0]?.NoteText||'';
      setItems(prev=>prev.map(row=>row.WorkListItemID===notesRow.WorkListItemID?{...row,ItemNote:latest,NoteCount:fresh.length}:row));
      setNotesRow(prev=>({...prev,ItemNote:latest,NoteCount:fresh.length}));
      alert('تم حذف الملاحظة');
    }catch(e){alert(e.message,'error');}
  };

  const getExportRows = async () => {
    if(!activeList) return [];
    const params=new URLSearchParams();
    if(itemSearch.trim()) params.set('search',itemSearch.trim());
    if(itemStatusFilter) params.set('workStatus',itemStatusFilter);
    const data=await api(`${API}/${activeList.WorkListID}/export-data?${params.toString()}`);
    if(data?.truncated) alert('تم تصدير أول 20,000 سجل مطابق فقط','warning');
    return Array.isArray(data?.data)?data.data:[];
  };

  const exportExcel = async () => {
    try{
      setExporting(true);
      const rows=await getExportRows();
      if(!rows.length) return alert('لا توجد بيانات للتصدير','error');
      const data=rows.map((row,index)=>({
        'م':index+1,
        'الاسم':row.NameArabic||'',
        'أرقام الهاتف':getPhones(row).join(' / '),
        'الشركة':row.CompanyName||'',
        'المندوب':row.DelegateName||'',
        'المصدر':row.SourceType==='TEST'?'اختبارات':'جوازات',
        'اسم المصدر':row.SourceLabel||'',
        'الحالة':statusMeta[row.WorkStatus]?.label||row.WorkStatus||'',
        'الملاحظات':row.AllNotes||row.ItemNote||'',
        'آخر ملاحظة':row.ItemNote||'',
        'عدد الملاحظات':Number(row.NoteCount||0)
      }));
      const ws=XLSX.utils.json_to_sheet(data);
      const wb=XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb,ws,'قائمة العمل');
      XLSX.writeFile(wb,`${String(activeList.ListName||'قائمة-عمل').replace(/[\\/:*?"<>|]/g,'-')}.xlsx`);
      alert(`تم تصدير ${rows.length} شخص`);
    }catch(e){alert(e.message,'error');}
    finally{setExporting(false);}
  };

  const printList = async () => {
    try{
      setExporting(true);
      const rows=await getExportRows();
      if(!rows.length) return alert('لا توجد بيانات للطباعة','error');
      const body=rows.map((row,index)=>`<tr><td>${index+1}</td><td>${escapeHtml(row.NameArabic||'-')}</td><td dir="ltr">${escapeHtml(getPhones(row).join(' / ')||'-')}</td><td>${escapeHtml(row.CompanyName||'-')}</td><td>${escapeHtml(row.DelegateName||'-')}</td><td>${escapeHtml(statusMeta[row.WorkStatus]?.label||'-')}</td><td>${escapeHtml(row.AllNotes||row.ItemNote||'-')}</td></tr>`).join('');
      const w=window.open('','_blank','width=1200,height=800');
      if(!w) throw new Error('المتصفح منع نافذة الطباعة');
      w.document.write(`<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"><title>${escapeHtml(activeList.ListName)}</title><style>@page{size:A4 landscape;margin:8mm}*{box-sizing:border-box}body{font-family:Segoe UI,Tahoma,sans-serif;color:#111;margin:0}.head{text-align:center;border-bottom:3px solid #111;padding-bottom:12px;margin-bottom:14px}.head h1{margin:0 0 8px;font-size:26px}.meta{font-size:14px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #222;padding:7px 5px;text-align:center;font-size:12px}th{background:#eee;font-weight:800}tr:nth-child(even){background:#fafafa}.foot{margin-top:12px;text-align:center;font-size:11px;color:#555}@media print{.no-print{display:none}}</style></head><body><div class="head"><h1>${escapeHtml(activeList.ListName)}</h1><div class="meta">إجمالي: ${rows.length.toLocaleString('ar-EG')} • ${new Date().toLocaleString('ar-EG')}</div></div><table><thead><tr><th>م</th><th>الاسم</th><th>الهاتف</th><th>الشركة</th><th>المندوب</th><th>الحالة</th><th>الملاحظات</th></tr></thead><tbody>${body}</tbody></table><div class="foot">قائمة عمل — نظام مسار</div><script>window.onload=()=>setTimeout(()=>window.print(),150)</script></body></html>`);
      w.document.close();
    }catch(e){alert(e.message,'error');}
    finally{setExporting(false);}
  };

  const visiblePageNumbers = useMemo(()=>{
    const total=Math.max(1,Number(pagination.totalPages)||1);
    const current=Math.min(total,Math.max(1,Number(page)||1));
    const start=Math.max(1,Math.min(current-2,total-4));
    const end=Math.min(total,start+4);
    return Array.from({length:end-start+1},(_,i)=>start+i);
  },[pagination.totalPages,page]);

  const statusMeta = {
    pending:{label:'مطلوب إجراء',icon:Clock3,className:'bg-amber-50 text-amber-700 border-amber-200'},
    doing:{label:'جاري',icon:PlayCircle,className:'bg-blue-50 text-blue-700 border-blue-200'},
    done:{label:'تم',icon:CheckCircle2,className:'bg-emerald-50 text-emerald-700 border-emerald-200'}
  };

  const currentCounts = activeList ? {
    total:Number(activeList.TotalItems||0),
    done:Number(activeList.DoneItems||0),
    doing:Number(activeList.DoingItems||0),
    pending:Number(activeList.PendingItems||0)
  } : {total:0,done:0,doing:0,pending:0};

  if(!can('عرض قوائم العمل')) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" dir="rtl"><div className="bg-white border rounded-3xl p-10 text-center max-w-lg"><Lock className="mx-auto text-slate-400 mb-3" size={38}/><div className="font-black text-xl">ليس لديك صلاحية عرض قوائم العمل</div><button onClick={()=>navigate('/')} className="mt-5 px-4 py-2.5 bg-slate-900 text-white rounded-xl font-bold">العودة للرئيسية</button></div></div>;
  }

  return <div className="min-h-screen bg-slate-50 p-4 md:p-6" dir="rtl">
    {message&&<div className={`fixed top-4 left-1/2 -translate-x-1/2 z-[2000] px-5 py-3 rounded-2xl shadow-xl font-bold ${message.type==='error'?'bg-red-600 text-white':message.type==='warning'?'bg-amber-500 text-white':'bg-emerald-600 text-white'}`}>{message.text}</div>}

    <div className="max-w-7xl mx-auto">
      <div className="bg-white rounded-3xl border shadow-sm overflow-hidden mb-5">
        <div className="p-5 md:p-6 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <button onClick={()=>activeList?setActiveList(null):navigate('/')} className="p-2.5 rounded-xl hover:bg-slate-100"><ArrowRight size={20}/></button>
            <div>
              <h1 className="text-2xl font-black text-slate-900 flex items-center gap-2"><ListTodo className="text-blue-600"/> {activeList?activeList.ListName:'قوائم العمل'}</h1>
              <p className="text-sm text-slate-500 mt-1">{activeList?'تجميع بسيط للأشخاص المطلوب اتخاذ إجراء معهم.':'اجمع أشخاصًا من الاختبارات أو كشوف الجوازات في قائمة متابعة واحدة.'}</p>
            </div>
          </div>
          {!activeList&&can('إنشاء قائمة')&&<button onClick={openCreate} className="px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-black inline-flex items-center gap-2"><Plus size={18}/> قائمة عمل جديدة</button>}
          {activeList&&<div className="flex gap-2 flex-wrap">
            {can('تعديل القوائم')&&<button onClick={()=>openEdit(activeList)} className="px-3 py-2.5 rounded-xl border bg-white font-bold inline-flex items-center gap-2"><Edit2 size={17}/> تعديل</button>}
            {can('إغلاق القوائم')&&<button onClick={toggleListStatus} className="px-3 py-2.5 rounded-xl border bg-white font-bold inline-flex items-center gap-2">{activeList.ListStatus==='closed'?<Unlock size={17}/>:<Lock size={17}/>} {activeList.ListStatus==='closed'?'فتح القائمة':'إغلاق القائمة'}</button>}
            {can('تصدير Excel')&&<button disabled={exporting} onClick={exportExcel} className="px-3 py-2.5 rounded-xl border bg-white font-bold inline-flex items-center gap-2 disabled:opacity-50"><FileSpreadsheet size={17}/> Excel</button>}
            {can('طباعة')&&<button disabled={exporting} onClick={printList} className="px-3 py-2.5 rounded-xl border bg-white font-bold inline-flex items-center gap-2 disabled:opacity-50"><Printer size={17}/> طباعة</button>}
          </div>}
        </div>
      </div>

      {!activeList ? <>
        <div className="bg-white rounded-2xl border p-4 mb-4 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[230px]"><Search size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"/><input disabled={!can('البحث والفلترة')} value={listSearch} onChange={e=>setListSearch(e.target.value)} placeholder="ابحث باسم قائمة العمل..." className="w-full pr-10 pl-3 py-2.5 rounded-xl border outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"/></div>
          <div className="flex rounded-xl border overflow-hidden">{[['open','المفتوحة'],['closed','المنتهية'],['','الكل']].map(([v,l])=><button disabled={!can('البحث والفلترة')} key={v||'all'} onClick={()=>setListFilter(v)} className={`px-4 py-2.5 font-bold disabled:opacity-50 ${listFilter===v?'bg-slate-900 text-white':'bg-white text-slate-600 hover:bg-slate-50'}`}>{l}</button>)}</div>
        </div>

        {loading?<div className="py-20 text-center text-slate-500"><RefreshCw className="animate-spin mx-auto mb-3"/>جاري تحميل القوائم...</div>:lists.length?<div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{lists.map(list=>{
          const total=Number(list.TotalItems||0),done=Number(list.DoneItems||0),remaining=Math.max(0,total-done);
          return <div key={list.WorkListID} className="bg-white rounded-3xl border shadow-sm overflow-hidden hover:shadow-md transition">
            <div className="h-2" style={{background:list.ColorHex||'#2563EB'}}/>
            <div className="p-5">
              <div className="flex items-start justify-between gap-3"><div><h3 className="font-black text-lg text-slate-900">{list.ListName}</h3><div className="mt-1 text-xs font-bold"><span className={`px-2 py-1 rounded-full ${list.ListStatus==='closed'?'bg-slate-100 text-slate-600':'bg-emerald-50 text-emerald-700'}`}>{list.ListStatus==='closed'?'منتهية':'مفتوحة'}</span></div></div><div className="text-3xl font-black" style={{color:list.ColorHex||'#2563EB'}}>{total.toLocaleString('ar-EG')}</div></div>
              <div className="grid grid-cols-3 gap-2 mt-5 text-center"><div className="rounded-xl bg-amber-50 p-2"><div className="text-xs text-amber-700 font-bold">متبقي</div><div className="font-black text-amber-800">{remaining.toLocaleString('ar-EG')}</div></div><div className="rounded-xl bg-blue-50 p-2"><div className="text-xs text-blue-700 font-bold">جاري</div><div className="font-black text-blue-800">{Number(list.DoingItems||0).toLocaleString('ar-EG')}</div></div><div className="rounded-xl bg-emerald-50 p-2"><div className="text-xs text-emerald-700 font-bold">تم</div><div className="font-black text-emerald-800">{done.toLocaleString('ar-EG')}</div></div></div>
              <div className="flex gap-2 mt-5">{can('فتح قائمة')&&<button onClick={()=>setActiveList(list)} className="flex-1 px-4 py-2.5 rounded-xl bg-slate-900 text-white font-black">فتح القائمة</button>}{can('تعديل القوائم')&&<button onClick={()=>openEdit(list)} className="p-2.5 rounded-xl border hover:bg-slate-50"><Edit2 size={18}/></button>}{can('حذف القوائم')&&<button onClick={()=>deleteList(list)} className="p-2.5 rounded-xl border text-red-600 hover:bg-red-50"><Trash2 size={18}/></button>}</div>
            </div>
          </div>;
        })}</div>:<div className="bg-white border-2 border-dashed rounded-3xl py-20 text-center text-slate-500"><ListTodo size={42} className="mx-auto mb-3 text-slate-300"/><div className="font-black">لا توجد قوائم عمل هنا</div><div className="text-sm mt-1">أنشئ قائمة جديدة ثم أضف لها أشخاصًا من الاختبارات أو كشف الجوازات.</div></div>}
      </> : <>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4"><div className="bg-white border rounded-2xl p-4"><div className="text-xs font-bold text-slate-500">الإجمالي</div><div className="text-2xl font-black mt-1">{currentCounts.total.toLocaleString('ar-EG')}</div></div><div className="bg-white border rounded-2xl p-4"><div className="text-xs font-bold text-amber-600">مطلوب إجراء</div><div className="text-2xl font-black text-amber-700 mt-1">{currentCounts.pending.toLocaleString('ar-EG')}</div></div><div className="bg-white border rounded-2xl p-4"><div className="text-xs font-bold text-blue-600">جاري</div><div className="text-2xl font-black text-blue-700 mt-1">{currentCounts.doing.toLocaleString('ar-EG')}</div></div><div className="bg-white border rounded-2xl p-4"><div className="text-xs font-bold text-emerald-600">تم</div><div className="text-2xl font-black text-emerald-700 mt-1">{currentCounts.done.toLocaleString('ar-EG')}</div></div></div>

        <div className="bg-white border rounded-2xl p-4 mb-4 flex gap-3 flex-wrap items-center"><div className="relative flex-1 min-w-[220px]"><Search size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"/><input disabled={!can('البحث والفلترة')} value={itemSearch} onChange={e=>setItemSearch(e.target.value)} placeholder="ابحث بالاسم أو الهاتف أو الشركة أو المندوب أو الملاحظات..." className="w-full pr-10 pl-3 py-2.5 rounded-xl border outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"/></div><select disabled={!can('البحث والفلترة')} value={itemStatusFilter} onChange={e=>setItemStatusFilter(e.target.value)} className="px-3 py-2.5 rounded-xl border bg-white font-bold disabled:bg-slate-100"><option value="">كل الحالات</option><option value="pending">مطلوب إجراء</option><option value="doing">جاري</option><option value="done">تم</option></select><button onClick={()=>loadItems(page)} className="p-2.5 rounded-xl border bg-white" title="تحديث"><RefreshCw size={18}/></button></div>

        <div className="bg-white border rounded-3xl overflow-hidden shadow-sm">
          {itemsLoading?<div className="py-16 text-center text-slate-500"><RefreshCw className="animate-spin mx-auto mb-2"/>جاري تحميل الأشخاص...</div>:items.length?<div className="overflow-x-auto"><table className="w-full text-sm"><thead className="bg-slate-100 text-slate-700"><tr><th className="p-3 text-right">م</th><th className="p-3 text-right">الاسم</th><th className="p-3 text-right">الهاتف</th><th className="p-3 text-right">الشركة</th><th className="p-3 text-right">المندوب</th><th className="p-3 text-right">المصدر</th><th className="p-3 text-right">الحالة</th><th className="p-3 text-right min-w-[230px]">الملاحظات</th><th className="p-3 text-right">إجراء</th></tr></thead><tbody className="divide-y">{items.map((row,index)=>{
            const phones=getPhones(row);
            return <tr key={row.WorkListItemID} className="hover:bg-slate-50"><td className="p-3 font-bold text-slate-500">{(pagination.from||1)+index}</td><td className="p-3"><div className="font-black text-slate-900">{row.NameArabic||'سجل غير متاح'}</div>{row.SourceIsActive===false||row.SourceIsActive===0?<div className="text-xs text-red-500 mt-1">السجل الأصلي مؤرشف</div>:null}</td><td className="p-3"><div className="space-y-1">{phones.length?phones.map((p,i)=><div key={i} dir="ltr" className="font-bold text-slate-700">{p}</div>):'-'}</div></td><td className="p-3 font-bold">{row.CompanyName||'-'}</td><td className="p-3">{row.DelegateName||'-'}</td><td className="p-3"><span className={`px-2 py-1 rounded-full text-xs font-bold ${row.SourceType==='TEST'?'bg-blue-50 text-blue-700':'bg-emerald-50 text-emerald-700'}`}>{row.SourceType==='TEST'?'اختبارات':'جوازات'}</span><div className="text-xs text-slate-400 mt-1 max-w-[160px] truncate" title={row.SourceLabel||''}>{row.SourceLabel||''}</div></td><td className="p-3"><div className="flex flex-col gap-1">{Object.entries(statusMeta).map(([key,m])=>{const Icon=m.icon;return <button key={key} disabled={!can('تحديث حالة الشخص')} onClick={()=>updateItem(row,{WorkStatus:key},`تم تغيير حالة ${row.NameArabic||'الشخص'}`)} className={`px-2 py-1.5 rounded-lg border text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-45 ${row.WorkStatus===key?m.className:'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}><Icon size={14}/>{m.label}</button>})}</div></td><td className="p-3"><button disabled={!can('عرض الملاحظات',['تحديث حالة الشخص'])} onClick={()=>openNotes(row)} className="w-full text-right p-2.5 rounded-xl border bg-white hover:bg-slate-50 disabled:opacity-50"><div className="flex items-center justify-between gap-2"><span className="font-bold text-slate-700">ملاحظات ({Number(row.NoteCount||0)})</span><MessageSquarePlus size={15} className="text-blue-600"/></div><div className="text-xs text-slate-500 mt-1 line-clamp-2">{row.ItemNote||'لا توجد ملاحظات'}</div></button></td><td className="p-3">{can('إزالة أشخاص')&&<button onClick={()=>removeItem(row)} className="p-2 rounded-lg text-red-600 hover:bg-red-50" title="إزالة من القائمة"><Trash2 size={17}/></button>}</td></tr>;
          })}</tbody></table></div>:<div className="py-16 text-center text-slate-500"><UsersEmpty/></div>}

          {pagination.totalPages>1&&<div className="p-4 border-t bg-slate-50 flex items-center justify-between gap-3 flex-wrap"><div className="text-sm text-slate-500 font-bold">عرض {pagination.from.toLocaleString('ar-EG')}–{pagination.to.toLocaleString('ar-EG')} من {pagination.total.toLocaleString('ar-EG')}</div><div className="flex items-center gap-1"><button disabled={page<=1||itemsLoading} onClick={()=>loadItems(1)} className="px-3 py-2 rounded-lg border bg-white disabled:opacity-40">الأولى</button><button disabled={page<=1||itemsLoading} onClick={()=>loadItems(page-1)} className="p-2 rounded-lg border bg-white disabled:opacity-40"><ChevronRight size={17}/></button>{visiblePageNumbers.map(n=><button disabled={itemsLoading} key={n} onClick={()=>loadItems(n)} className={`min-w-9 px-2 py-2 rounded-lg border font-bold ${n===page?'bg-slate-900 text-white border-slate-900':'bg-white'}`}>{n}</button>)}<button disabled={page>=pagination.totalPages||itemsLoading} onClick={()=>loadItems(page+1)} className="p-2 rounded-lg border bg-white disabled:opacity-40"><ChevronLeft size={17}/></button><button disabled={page>=pagination.totalPages||itemsLoading} onClick={()=>loadItems(pagination.totalPages)} className="px-3 py-2 rounded-lg border bg-white disabled:opacity-40">الأخيرة</button></div></div>}
        </div>
      </>}
    </div>

    <Modal open={showListModal} onClose={()=>setShowListModal(false)} title={editingList?'تعديل قائمة العمل':'قائمة عمل جديدة'} footer={<div className="flex justify-end gap-2"><button onClick={()=>setShowListModal(false)} className="px-4 py-2.5 rounded-xl border bg-white font-bold">إلغاء</button><button disabled={saving} onClick={saveList} className="px-4 py-2.5 rounded-xl bg-blue-600 text-white font-black inline-flex items-center gap-2"><Save size={17}/>{saving?'جارٍ الحفظ...':'حفظ'}</button></div>}>
      <label className="block text-sm font-bold text-slate-700 mb-2">اسم القائمة</label><input value={listForm.ListName} onChange={e=>setListForm(prev=>({...prev,ListName:e.target.value}))} onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();saveList();}}} autoFocus placeholder="مثال: تجهيز عقود ركاب" className="w-full px-3 py-2.5 rounded-xl border mb-4 outline-none focus:ring-2 focus:ring-blue-100"/>
      <label className="block text-sm font-bold text-slate-700 mb-2">لون بسيط</label><div className="flex flex-wrap gap-2">{COLORS.map(color=><button key={color} type="button" onClick={()=>setListForm(prev=>({...prev,ColorHex:color}))} className={`w-10 h-10 rounded-full border-4 ${listForm.ColorHex===color?'border-slate-900':'border-white shadow'}`} style={{background:color}}/>)}</div>
    </Modal>

    <Modal open={!!notesRow} onClose={()=>setNotesRow(null)} title={`ملاحظات ${notesRow?.NameArabic||''}`} width="max-w-2xl">
      {can('إضافة ملاحظة',['تحديث حالة الشخص'])&&<div className="mb-5 p-4 rounded-2xl bg-blue-50 border border-blue-100"><label className="block text-sm font-black text-blue-900 mb-2">إضافة ملاحظة جديدة</label><textarea rows="3" value={newNote} onChange={e=>setNewNote(e.target.value)} placeholder="اكتب الملاحظة..." className="w-full p-3 rounded-xl border bg-white outline-none focus:ring-2 focus:ring-blue-100"/><div className="mt-2 flex justify-end"><button disabled={noteSaving||!newNote.trim()} onClick={addNote} className="px-4 py-2.5 rounded-xl bg-blue-600 text-white font-black inline-flex items-center gap-2 disabled:opacity-50">{noteSaving?<RefreshCw className="animate-spin" size={16}/>:<Plus size={16}/>} إضافة الملاحظة</button></div></div>}
      {notesLoading?<div className="py-12 text-center text-slate-500"><RefreshCw className="animate-spin mx-auto mb-2"/>جاري تحميل الملاحظات...</div>:notes.length?<div className="space-y-3">{notes.map(note=><div key={note.NoteID} className="border rounded-2xl p-4 bg-white"><div className="flex items-start justify-between gap-3"><div className="flex-1"><div className="font-bold text-slate-800 whitespace-pre-wrap">{note.NoteText}</div><div className="text-xs text-slate-400 mt-2">{new Date(note.CreatedAt).toLocaleString('ar-EG')}{note.CreatedByUserName?` • ${note.CreatedByUserName}`:note.CreatedByUserID?` • مستخدم ${note.CreatedByUserID}`:''}</div></div>{can('حذف ملاحظة')&&<button onClick={()=>deleteNote(note)} className="p-2 rounded-lg text-red-600 hover:bg-red-50" title="حذف الملاحظة"><Trash2 size={16}/></button>}</div></div>)}</div>:<div className="py-10 text-center text-slate-500">لا توجد ملاحظات مسجلة لهذا الشخص.</div>}
    </Modal>
  </div>;
};

const UsersEmpty = () => <><ListTodo size={42} className="mx-auto mb-3 text-slate-300"/><div className="font-black">لا يوجد أشخاص في هذه القائمة</div><div className="text-sm mt-1">حدد أشخاصًا من كشف الاختبارات أو كشف الجوازات ثم اختر «إضافة إلى قائمة عمل».</div></>;

export default WorkLists;
